<script lang="ts">
  import Am5Loader from '@/Util/Am5Loader.svelte';
  import { onDestroy } from 'svelte';
  import type { Writable } from 'svelte/store';
  import { getXertAm5, type am5Root, type am5xyChart } from '@/Util/XertAm5';
  import { RACE_EVENT_COLORS } from '@/Common/activityTagOptions';
  import { axisLayout, breakthroughBalloonHtml, eventBalloonHtml, checkpointLabel, formatDay, formatSignature, yearColor, type ChartView, type Metric, type Season, type SeasonDay, type SigKey } from './seasons';

  export let seasons: Record<string, Season>;
  export let years: number[];
  export let metric: Metric;
  export let sigKey: SigKey | null = null; // Adds a breakthrough dot for this signature parameter
  // 'signature' plots the predicted sigKey value instead of the training load (only on cards with a sigKey).
  export let view: ChartView = 'load';
  export let unitLabel = 'TL';
  export let startMonth: number;
  export let endMonth: number;
  // Shared across the cards so every guide moves in unison; `from` avoids feedback loops.
  export let hover: Writable<{ x: number | null; from: string }>;
  export let checkpoint: Writable<number | null>;
  export let checkpointHint = false; // Show "Click for Checkpoint" on the guide while no checkpoint is set

  const chartId = `season-chart-${Math.random().toString(36).slice(2)}`;
  // Touch screens can't hover: their hint is a fixed "Tap for Checkpoint" at the chart's right edge instead of following the pointer.
  const isTouch = typeof window !== 'undefined' && !!window.matchMedia?.('(hover: none)').matches;
  const FUTURE_OPACITY = 0.4;
  // amCharts' line simplification: points closer than this many pixels are skipped (as on the XPMC chart).
  const LINE_MIN_DISTANCE = 6;
  const BREAKTHROUGH_COLOR = '#00C853';
  const EVENT_COLORS = RACE_EVENT_COLORS; // The target icons use the Race / Event tag pills' colours.

  let chartElement: HTMLElement;
  let root: am5Root | undefined;
  let chart: am5xyChart | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let xAxis: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let cursor: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let checkpointLine: any;
  let yBounds = { min: 0, max: 1 };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let topLabel: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let lineSeries: any[] = [];
  let bulletClicked = false;
  $: plotsSignature = view === 'signature' && sigKey != null;
  // The plotted value for a day: its training load, or its predicted signature (null before there's a seed for it).
  $: valueOf = (d: SeasonDay): number | null => (plotsSignature ? d.sg[sigKey!] : d[metric]);
  // Rebuild the whole chart when the data or the shape of the season changes; the data is small.
  $: if (chartElement && seasons && years) rebuild(seasons, years, metric, sigKey, startMonth, endMonth, valueOf);
  $: applyCheckpoint($checkpoint);
  $: updateTopLabel($hover, $checkpoint);
  $: applyHover($hover);

  function rebuild(...args: unknown[]) {
    void args;
    root?.dispose();
    root = undefined;
    buildChart();
  }

  function buildChart() {
    const { am5, am5xy, MyTheme } = getXertAm5();
    am5.addLicense('AM5C87942260');

    root = am5.Root.new(chartElement);
    root.setThemes([MyTheme.new(root)]);
    chart = root.container.children.push(
      am5xy.XYChart.new(root, { layout: root.verticalLayout, paddingTop: 28, paddingRight: 16, paddingBottom: 26, paddingLeft: 0 }),
    );

    const layout = axisLayout(startMonth, endMonth);

    // X axis: day offset in the season, labelled at each month boundary.
    const xRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 40 });
    xRenderer.labels.template.set('forceHidden', true);
    xRenderer.grid.template.set('forceHidden', true);
    xAxis = chart.xAxes.push(
      am5xy.ValueAxis.new(root, { renderer: xRenderer, min: 0, max: layout.max, strictMinMax: true, maxPrecision: 0 }),
    );
    for (const tick of layout.ticks) {
      const item = xAxis.makeDataItem({ value: tick.x });
      xAxis.createAxisRange(item);
      // Range grids inherit the renderer template's forceHidden, so it has to be overridden on each one.
      item.get('grid')?.setAll({ strokeOpacity: 0.15, stroke: am5.color('#6B7280'), visible: true, forceHidden: false });
      item.get('label')?.setAll({ text: tick.label, visible: true, forceHidden: false, fontSize: 11, fill: am5.color('#6B7280'), paddingLeft: 12 });
    }

    const yRenderer = am5xy.AxisRendererY.new(root, {});
    yRenderer.grid.template.setAll({ strokeOpacity: 0.08, strokeDasharray: [3, 4] });
    yRenderer.labels.template.setAll({ fontSize: 11, fill: am5.color('#6B7280') });
    // Fit the y axis to the plotted data: just below the lowest value to just above the highest.
    let lo = Infinity;
    let hi = -Infinity;
    for (const year of years) {
      for (const d of seasons[String(year)]?.days ?? []) {
        const v = valueOf(d);
        if (d.leap || v == null) continue;
        lo = Math.min(lo, v);
        hi = Math.max(hi, v);
      }
    }
    if (!isFinite(lo)) [lo, hi] = [0, 1];
    const pad = Math.max((hi - lo) * 0.08, hi * 0.01, 0.1);
    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, { renderer: yRenderer, min: Math.max(0, lo - pad), max: hi + pad, strictMinMax: true }),
    );
    // Fixed label width so every card's plot area starts at the same x and the month ticks line up card to card.
    yAxis.ghostLabel.set('minWidth', 36);
    yBounds = { min: Math.max(0, lo - pad), max: hi + pad };

    // Checkpoint line: a two-point vertical series, so it doesn't depend on axis-range grid visibility.
    checkpointLine = chart.series.push(
      am5xy.LineSeries.new(root, { xAxis, yAxis, valueXField: 'x', valueYField: 'y', stroke: am5.color('#2563EB') }),
    );
    checkpointLine.strokes.template.setAll({ strokeWidth: 2, strokeOpacity: 0.9 });

    // The date under the guide, shown on the x axis by the cursor (seasons align by calendar day, so it holds for every year).
    const axisTooltip = am5.Tooltip.new(root, { getFillFromSprite: false, autoTextColor: false });
    axisTooltip.get('background')?.setAll({ fill: am5.color('#374151'), fillOpacity: 1, strokeOpacity: 0 });
    axisTooltip.label.setAll({ fill: am5.color('#ffffff'), fontSize: 11 });
    xAxis.getTooltipText = (position: number) => checkpointLabel(Math.round(xAxis.positionToValue(position)), startMonth);
    xAxis.set('tooltip', axisTooltip);

    cursor = chart.set('cursor', am5xy.XYCursor.new(root, { behavior: 'none', xAxis }));
    cursor.lineY.set('visible', false);
    cursor.lineX.setAll({ strokeDasharray: [4, 3], strokeOpacity: 0.5, stroke: am5.color('#6B7280') });

    lineSeries = [];
    for (const year of years) {
      const season = seasons[String(year)];
      if (!season) continue;
      const color = yearColor(year);
      const drawn = season.days.filter((d) => !d.leap && valueOf(d) != null);
      const points = drawn.map((d) => ({ x: d.x, y: valueOf(d) as number, future: d.future, held: !!d.held }));
      const pastPoints = points.filter((p) => !p.future);
      const heldPoints = points.filter((p) => p.future && p.held);
      const futurePoints = points.filter((p) => p.future && !p.held);
      // Each later segment starts at the last point of the one before so the line is continuous.
      const heldData = pastPoints.length && heldPoints.length ? [pastPoints[pastPoints.length - 1], ...heldPoints] : heldPoints;
      const beforeFuture = heldPoints.length ? heldPoints[heldPoints.length - 1] : pastPoints[pastPoints.length - 1];
      const futureData = beforeFuture && futurePoints.length ? [beforeFuture, ...futurePoints] : futurePoints;

      const makeSeries = (name: string, data: typeof points, opacity: number, dashed = false) => {
        const series = chart!.series.push(
          am5xy.SmoothedXLineSeries.new(root!, { name, xAxis, yAxis, valueXField: 'x', valueYField: 'y', stroke: am5.color(color), fill: am5.color(color), tension: 0.1, minDistance: LINE_MIN_DISTANCE }),
        );
        // Solid lines; only the planned (future) part of a season is lighter.
        series.strokes.template.setAll({ strokeWidth: 2.5, strokeOpacity: opacity, ...(dashed ? { strokeDasharray: [2, 4], strokeLinecap: 'round' } : {}) });
        series.data.setAll(data);
        return series;
      };
      if (pastPoints.length) lineSeries.push(makeSeries(`${year}`, pastPoints, 1));
      if (heldData.length) lineSeries.push(makeSeries(`${year} (maintained)`, heldData, FUTURE_OPACITY, true));
      if (futureData.length) lineSeries.push(makeSeries(`${year} (planned)`, futureData, FUTURE_OPACITY));

      // Highest value on the drawn line. Taken from the drawn days rather than the whole season, so a high on an
      // undrawn leap day can't make the dot disappear.
      const highDay = drawn.reduce<SeasonDay | null>((best, d) => (best == null || (valueOf(d) as number) > (valueOf(best) as number) ? d : best), null);
      if (highDay) {
        const high = valueOf(highDay) as number;
        const label = plotsSignature ? formatSignature(high, sigKey!) : `${high.toFixed(1)} ${unitLabel}`;
        addDot(highDay.x, high, color, 6, `Season high ${label}\n${formatDay(highDay.d)}`, highDay.future ? FUTURE_OPACITY : 1);
      }

      // Highest breakthrough in the season, on that day's plotted value. Opens the activity on click. When that
      // activity is also a Race or Event it is drawn as a diamond with a target inside and says so in its balloon (and gets no target of its own).
      const bt = sigKey ? season.breakthroughs[sigKey] : null;
      const events = season.events ?? [];
      let targetInDiamond = false;
      const btEvent = bt ? events.find((event) => event.path === bt.path) : undefined;
      if (sigKey && bt) {
        const btDay = season.days.find((d) => d.d === bt.date);
        const btY = btDay ? valueOf(btDay) : null;
        if (btDay && btY != null) {
          targetInDiamond = !!btEvent;
          addDot(btDay.x, btY, BREAKTHROUGH_COLOR, btEvent ? 9 : 6, '', 1, bt.path, breakthroughBalloonHtml(bt, btEvent?.kind), btEvent ? EVENT_COLORS[btEvent.kind] : undefined);
        }
      }

      // Races and events, on that day's plotted value. Opens the activity on click, as the breakthrough diamond does.
      for (const event of events) {
        if (targetInDiamond && event === btEvent) continue;
        const eventDay = season.days.find((d) => d.d === event.date && !d.leap);
        const eventY = eventDay ? valueOf(eventDay) : null;
        if (eventDay && eventY != null) addTarget(eventDay.x, eventY, EVENT_COLORS[event.kind], event.path, eventBalloonHtml(event));
      }
    }

    cursor.set('snapToSeries', lineSeries);
    cursor.set('snapToSeriesBy', 'x');

    // Drive the shared guide from real pointer movement only, so remote updates can't echo back.
    const plot = chart.plotContainer;
    const xFromEvent = (point: { x: number; y: number }) => {
      const local = plot.toLocal(point);
      const pos = Math.max(0, Math.min(1, local.x / plot.width()));
      return Math.round(xAxis.positionToValue(xAxis.toAxisPosition(pos)));
    };
    // One label in the strip above the plot: the checkpoint's date, or (Total chart only) the "Click for Checkpoint"
    // hint while hovering. It lives above the plot so nothing on the guide can cover it.
    topLabel = root.container.children.push(
      am5.Label.new(root, {
        text: '',
        fontSize: 11,
        fill: am5.color('#ffffff'),
        y: 3,
        centerX: am5.p50,
        visible: false,
        cursorOverStyle: 'pointer',
        paddingTop: 2,
        paddingBottom: 2,
        paddingLeft: 8,
        paddingRight: 8,
        background: am5.RoundedRectangle.new(root, { fill: am5.color('#2563EB'), fillOpacity: 1 }),
      }),
    );

    // There is deliberately no hover popover listing each year's load and predicted signature for the day: it followed the
    // pointer over the plot and so covered the Race/Event targets and breakthrough diamonds, making them unclickable. The
    // checkpoint (click the plot) gives those values per year in the summary table instead.
    // The layout keeps settling after the chart is built (grid columns, fonts, a checkpoint restored from session
    // storage). A line built while the plot is still ~0px wide keeps only the few points that survive minDistance and
    // stays that way, so recompute the series' points whenever the plot's size changes. markDirty() would not do this:
    // only markDirtyValues() makes the series rebuild its geometry.
    plot.events.on('boundschanged', () => {
      updateTopLabel($hover, $checkpoint);
      checkpointLine?.markDirtyValues();
      lineSeries.forEach((s) => s.markDirtyValues());
    });

    let pointerOver = false;
    plot.events.on('pointerover', () => (pointerOver = true));
    // The checkpoint's date pill carries an ×; clicking it removes the checkpoint (the hint pill isn't interactive).
    topLabel.events.on('click', () => {
      if ($checkpoint == null) return;
      checkpoint.set(null);
      hover.set({ x: null, from: '' });
    });

    // globalpointermove fires for the whole page, so only follow it while over this plot.
    plot.events.on('globalpointermove', (ev) => {
      if (pointerOver) hover.set({ x: xFromEvent(ev.point), from: chartId });
    });
    plot.events.on('pointerout', (ev) => {
      pointerOver = false;
      // Keep the guide after a tap on touch screens; otherwise it would vanish as the finger lifts.
      if ((ev.originalEvent as PointerEvent)?.pointerType === 'touch') return;
      hover.set({ x: null, from: chartId });
    });
    plot.events.on('click', (ev) => {
      if (bulletClicked) return;
      const x = xFromEvent(ev.point);
      hover.set({ x, from: chartId }); // A tap moves the guide, as hovering does with a mouse
      checkpoint.set(x);
    });

    applyCheckpoint($checkpoint);
    // No appear() animation: half-finished entry animations were a likely cause of series staying invisible.
  }

  function addDot(x: number, y: number, color: string, radius: number, tooltipText: string, opacity: number, activityPath?: string, balloonHtml?: string, targetColor?: string) {
    const { am5, am5xy } = getXertAm5();
    const yAxis = chart!.yAxes.getIndex(0)!;
    const series = chart!.series.push(
      am5xy.LineSeries.new(root!, { xAxis, yAxis: yAxis as never, valueXField: 'x', valueYField: 'y', stroke: am5.color(color), fill: am5.color(color) }),
    );
    series.strokes.template.set('visible', false);
    series.data.setAll([{ x, y }]);
    series.bullets.push((bulletRoot) => {
      let sprite;
      if (activityPath && targetColor) {
        // A breakthrough that is also a Race/Event: the green-edged diamond with the tag's target (a ring and a dot,
        // as diamonds) inside it. Only the outer diamond takes the pointer.
        const diamond = (side: number, look: Record<string, unknown>) =>
          am5.Rectangle.new(bulletRoot, { width: side, height: side, centerX: am5.p50, centerY: am5.p50, rotation: 45, ...look });
        const outer = diamond(radius * 2, { fill: am5.color('#ffffff'), stroke: am5.color(color), strokeWidth: 3, cursorOverStyle: 'pointer', tooltipHTML: balloonHtml, tooltip: balloonTooltip(bulletRoot) });
        bindBalloon(outer, activityPath);
        sprite = am5.Container.new(bulletRoot, {});
        sprite.children.push(outer);
        sprite.children.push(diamond(radius * 1.2, { fill: am5.color('#ffffff'), stroke: am5.color(targetColor), strokeWidth: 1.5 }));
        sprite.children.push(diamond(radius * 0.5, { fill: am5.color(targetColor) }));
      } else if (activityPath) {
        sprite = am5.Rectangle.new(bulletRoot, { width: radius * 2, height: radius * 2, centerX: am5.p50, centerY: am5.p50, rotation: 45, fill: am5.color(color), stroke: am5.color('#166534'), strokeWidth: 1.5, cursorOverStyle: 'pointer', tooltipHTML: balloonHtml, tooltip: balloonTooltip(bulletRoot) });
        bindBalloon(sprite, activityPath);
      } else {
        sprite = am5.Circle.new(bulletRoot, { radius, fill: am5.color(color), fillOpacity: opacity, stroke: am5.color('#ffffff'), strokeWidth: 2, tooltipText });
      }
      return am5.Bullet.new(bulletRoot, { sprite });
    });
  }

  // A target: a ring inside a ring around a dot, in the tag's colour, with a balloon like the diamond's.
  function addTarget(x: number, y: number, color: string, activityPath: string, balloonHtml: string) {
    const { am5, am5xy } = getXertAm5();
    const yAxis = chart!.yAxes.getIndex(0)!;
    const series = chart!.series.push(
      am5xy.LineSeries.new(root!, { xAxis, yAxis: yAxis as never, valueXField: 'x', valueYField: 'y', stroke: am5.color(color), fill: am5.color(color) }),
    );
    series.strokes.template.set('visible', false);
    series.data.setAll([{ x, y }]);
    series.bullets.push((bulletRoot) => {
      // Only the outer disc takes the pointer; the rings and dot on it are drawn over it.
      const disc = am5.Circle.new(bulletRoot, { radius: 7, fill: am5.color('#ffffff'), stroke: am5.color(color), strokeWidth: 1.5, cursorOverStyle: 'pointer', tooltipHTML: balloonHtml, tooltip: balloonTooltip(bulletRoot) });
      bindBalloon(disc, activityPath);
      const sprite = am5.Container.new(bulletRoot, {});
      sprite.children.push(disc);
      sprite.children.push(am5.Circle.new(bulletRoot, { radius: 3.8, fill: am5.color('#ffffff'), stroke: am5.color(color), strokeWidth: 1.5 }));
      sprite.children.push(am5.Circle.new(bulletRoot, { radius: 1.5, fill: am5.color(color) }));
      return am5.Bullet.new(bulletRoot, { sprite });
    });
  }

  // A click opens the activity.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function bindBalloon(sprite: any, activityPath: string) {
    sprite.events.on('click', () => {
      bulletClicked = true;
      setTimeout(() => (bulletClicked = false), 0);
      window.open(`/activities/${activityPath}`, 'activity');
    });
  }

  // Light balloon like the XPMC chart's, rather than a tooltip tinted by the sprite.
  function balloonTooltip(bulletRoot: am5Root) {
    const { am5 } = getXertAm5();
    const tooltip = am5.Tooltip.new(bulletRoot, { getFillFromSprite: false, autoTextColor: false, pointerOrientation: 'vertical' });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (tooltip.get('background') as any).setAll({ fill: am5.color('#ffffff'), fillOpacity: 1, stroke: am5.color('#DADADA'), strokeOpacity: 1 });
    tooltip.label.setAll({ fill: am5.color('#000000'), fontSize: 12, maxWidth: 280, oversizedBehavior: 'wrap' });
    return tooltip;
  }

  function applyCheckpoint(x: number | null) {
    if (!checkpointLine) return;
    if (x == null) {
      checkpointLine.data.setAll([]);
    } else {
      checkpointLine.data.setAll([
        { x, y: yBounds.min },
        { x, y: yBounds.max },
      ]);
    }
    updateTopLabel($hover, x);
  }

  // Blue date pill for the checkpoint. Without one, the Total chart shows a grey hint: "Click for Checkpoint" following the
  // pointer on desktop, or a fixed "Tap for Checkpoint" at the right edge on touch screens. Kept inside the chart.
  function updateTopLabel(state: { x: number | null; from: string }, cp: number | null) {
    if (!topLabel || !xAxis || !chart) return;
    const touchHint = cp == null && checkpointHint && isTouch;
    const at = touchHint ? 0 : (cp ?? (checkpointHint ? state.x : null));
    if (at == null) {
      topLabel.set('visible', false);
      return;
    }
    const { am5 } = getXertAm5();
    const plot = chart.plotContainer;
    const pos = touchHint ? 1 : xAxis.toGlobalPosition(xAxis.valueToPosition(at));
    topLabel.get('background').set('fill', am5.color(cp != null ? '#2563EB' : '#374151'));
    topLabel.setAll({
      interactive: cp != null,
      text: cp != null ? `${checkpointLabel(cp, startMonth)}  ×` : isTouch ? 'Tap for Checkpoint' : 'Click for Checkpoint',
      x: plot.toGlobal({ x: pos * plot.width(), y: 0 }).x,
      centerX: touchHint || pos > 0.88 ? am5.p100 : pos < 0.12 ? am5.p0 : am5.p50,
      visible: true,
    });
  }

  // Mirror another chart's guide: move this cursor to the same x.
  function applyHover(state: { x: number | null; from: string }) {
    if (!cursor || !xAxis || state.from === chartId) return;
    if (state.x == null) {
      // Setting positionX to undefined doesn't hide the cursor, and on touch screens no pointer-out ever does. A plain
      // hide() gets undone (the cursor re-shows itself from its last point on the next layout change), so move it
      // outside the plot instead, which hides it and keeps it hidden.
      cursor.set('alwaysShow', false);
      cursor.set('positionX', undefined);
      cursor.handleMove({ x: -10000, y: -10000 }, true);
      return;
    }
    cursor.set('positionX', xAxis.toGlobalPosition(xAxis.valueToPosition(state.x)));
    cursor.set('alwaysShow', true);
  }

  onDestroy(() => {
    root?.dispose();
  });
</script>

<Am5Loader>
  <div bind:this={chartElement} class="h-[260px] w-full sm:h-[300px]"></div>
</Am5Loader>
